import { CircularProgress, Divider, Grid, Paper } from "@mui/material";
import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "components/atoms/text";
import { EngagementAccount, Post } from "core/types/social.types";
import { Post as PostCard } from "components/molecules/post";
import { InteractionGraphs } from "components/organisms/interaction-graphs";
import { KeyValueInsights } from "components/atoms/key-value-insights";
import { abbreviateNumber } from "core/helpers/abbreviate-number";
import { grey } from "@mui/material/colors";
import { PostAnalysis } from "components/molecules/post-analysis";

interface Props {
  isReportLoading: boolean;
  posts?: Post[];
  averageView?: string;
  averageReach?: string;
  averageInteractions?: string;
  engagementAccount?: EngagementAccount;
}

const PostsContent: FunctionComponent<Props> = ({
  isReportLoading,
  posts,
  averageView,
  averageInteractions,
  averageReach,
  engagementAccount,
}) => {
  const { t } = useTranslation();

  return (
    <Paper
      sx={{
        borderRadius: "10px",
        padding: "20px 0px",
      }}
    >
      <Text fontSize={"18px"} fontWeight={"600"} pb={"15px"}>
        {t("social-reports.posts.title")}
      </Text>
      {isReportLoading ? (
        <div
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <CircularProgress color="secondary" size={"100px"} />
        </div>
      ) : (
        <Grid container spacing={4}>
          <Grid
            item
            container
            spacing={5}
            xs={12}
            display={"flex"}
            justifyContent={"left"}
            alignItems={"center"}
          >
            <Grid item>
              <KeyValueInsights
                title={t("social-report-detail.content.avg_views")}
                content={
                  averageView
                    ? abbreviateNumber(Number(averageView))
                    : t("empty_states.empty_stats.title")
                }
                size="small"
              />
            </Grid>
            <Grid item>
              <KeyValueInsights
                title={t("social-report-detail.content.avg_interactions")}
                content={
                  averageInteractions
                    ? abbreviateNumber(Number(averageInteractions))
                    : t("empty_states.empty_stats.title")
                }
                size="small"
              />
            </Grid>
            <Grid item>
              <KeyValueInsights
                title={t("social-report-detail.content.avg_reach")}
                content={
                  averageReach
                    ? abbreviateNumber(Number(averageReach))
                    : t("empty_states.empty_stats.title")
                }
                size="small"
              />
            </Grid>
          </Grid>

          <Grid item container xs={12} spacing={4}>
            <Grid item xs={12}>
              <Text
                padding={"30px 0px 0px 0px"}
                fontWeight={700}
                style={{
                  color: grey[700],
                }}
                fontSize={"12px"}
              >
                {t("social-report-detail.content.egaged_accounts")}
              </Text>
              <Text fontSize={"10px"}>
                {engagementAccount ? engagementAccount.description : ""}
              </Text>
            </Grid>
            <Grid item container xs={12} sm={6} spacing={3}>
              <Grid item>
                <KeyValueInsights
                  title={t("social-report-detail.content.avg_per_day")}
                  content={
                    engagementAccount &&
                    engagementAccount.values.avgEngagedAccountsPerDay.value
                      ? abbreviateNumber(
                          Number(
                            engagementAccount.values.avgEngagedAccountsPerDay
                              .value
                          )
                        )
                      : t("empty_states.empty_stats.title")
                  }
                  size="small"
                />
              </Grid>
              <Grid item>
                <KeyValueInsights
                  title={t("social-report-detail.content.total_engage")}
                  content={
                    engagementAccount &&
                    engagementAccount.values.totalEngagedAccounts.value
                      ? abbreviateNumber(
                          Number(
                            engagementAccount.values.totalEngagedAccounts.value
                          )
                        )
                      : t("empty_states.empty_stats.title")
                  }
                  size="small"
                />
              </Grid>
            </Grid>
          </Grid>
          {posts &&
            posts.length > 0 &&
            posts?.map((post, idx) => {
              const valuesCount = {
                negative_count: post.negativeCount,
                positive_count: post.positiveCount,
                neutral_count: post.neutralCount,
              };

              return post.mostCommonEmotions && post.mostCommonIntents ? (
                <Grid key={idx} item container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    lg={4}
                    display={"flex"}
                    justifyContent={"center"}
                  >
                    <PostCard post={post.postInfo} />
                  </Grid>
                  <Grid item container xs={12} lg={8} spacing={2}>
                    <Grid item xs={12}>
                      <PostAnalysis
                        valuesCount={valuesCount}
                        commentsCount={post.commentsCount}
                        sentimentScale={post.sentimentScale}
                      />
                      <Divider sx={{ py: "5px" }} />
                    </Grid>

                    <Grid item xs={12}>
                      <InteractionGraphs
                        mostCommonEmotions={
                          Object.keys(post.mostCommonEmotions).length > 0
                            ? post.mostCommonEmotions
                            : {}
                        }
                        mostCommonIntents={
                          Object.keys(post.mostCommonIntents).length > 0
                            ? post.mostCommonIntents
                            : {}
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
              ) : (
                <Grid key={idx} item container spacing={2}>
                  <Grid item xs={12} lg={4}>
                    <PostCard post={post.postInfo} />
                  </Grid>
                  <Grid item xs={12} lg={8}>
                    {valuesCount &&
                      post.commentsCount &&
                      post.sentimentScale && (
                        <PostAnalysis
                          valuesCount={valuesCount}
                          commentsCount={post.commentsCount}
                          sentimentScale={post.sentimentScale}
                        />
                      )}
                  </Grid>
                </Grid>
              );
            })}
        </Grid>
      )}
    </Paper>
  );
};

export default PostsContent;
